import React, { FC, useMemo } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { CutoffSelectValue } from "./useCalendarEditState";
import { formatMinutes } from "./timeFormatUtils";

const SLOT_INTERVAL = 30;

type Props = {
  value: CutoffSelectValue;
  onChange: (value: CutoffSelectValue) => void;
  disabledValues?: string[];
  disabled?: boolean;
  slotStart: number;
  slotEnd: number;
};

const CutoffSelect: FC<Props> = ({
  value,
  onChange,
  disabledValues = [],
  disabled = false,
  slotStart,
  slotEnd,
}) => {
  const slots = useMemo(() => {
    const result: { value: string; label: string }[] = [];
    for (let m = slotStart; m <= slotEnd; m += SLOT_INTERVAL) {
      result.push({ value: String(m), label: formatMinutes(m) });
    }
    return result;
  }, [slotStart, slotEnd]);

  const disabledSet = useMemo(() => new Set(disabledValues), [disabledValues]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  return (
    <FormControl size="small" fullWidth disabled={disabled}>
      <InputLabel shrink>Read cutoff</InputLabel>
      <Select<string>
        value={value}
        label="Read cutoff"
        onChange={handleChange}
        notched
        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
      >
        <MenuItem value="available" disabled={disabledSet.has("available")}>
          Available
        </MenuItem>
        <MenuItem value="unavailable" disabled={disabledSet.has("unavailable")}>
          Unavailable
        </MenuItem>
        {slots.map(({ value: slotValue, label }) => (
          <MenuItem
            key={slotValue}
            value={slotValue}
            disabled={disabledSet.has(slotValue)}
          >
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CutoffSelect;
